import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Calendar, MapPin, Plus, X, Users, Trash2, MessageCircle, SlidersHorizontal, Trophy, CheckCircle2, EyeOff, Handshake, Share2, Bus, QrCode, UserPlus, Copy } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { CLUBS } from "@/data/clubs";
import { GROUNDS } from "@/data/grounds";
import { labelForConnection } from "@/data/connections";
import BottomNav from "@/components/app/BottomNav";
import EnableNotificationsNudge from "@/components/app/EnableNotificationsNudge";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const clubColor = (n) => CLUBS.find(c => c.name === n)?.color || "#152A55";
const initialsOf = (name) => (name || "?").split(" ").map(w => w[0]).slice(0,2).join("").toUpperCase();
const photoSrc = (url) => (url && url.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${url}` : url);

const formatKickoff = (iso) => {
  try {
    const d = new Date(iso);
    if (isNaN(d)) return iso;
    return d.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" }) +
      (iso.includes("T") ? " · " + d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" }) : "");
  } catch { return iso; }
};

export default function Fixtures() {
  const { user } = useAuth();
  const nav = useNavigate();
  const myTypes = user?.profile?.connection_types || [];
  const [tab, setTab] = useState("upcoming"); // "community" | "upcoming"
  const [fixtures, setFixtures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [pickerFor, setPickerFor] = useState(null); // fixture object when picking an attendee to DM
  const [activeFilters, setActiveFilters] = useState([]);
  const toggleFilter = (t) => setActiveFilters(f => f.includes(t) ? f.filter(x=>x!==t) : [...f, t]);
  const [checkinFor, setCheckinFor] = useState(null); // { fixture, source } — opens the check-in modal

  const connect = async (fixtureId, targetId) => {
    try {
      const { data } = await api.post(`/fixtures/${fixtureId}/connect/${targetId}`);
      toast.success(data.existed ? "Opening your chat…" : "You're connected — say hi!");
      setPickerFor(null);
      nav(`/chat/${data.match_id}`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not connect"); }
  };

  const load = async () => {
    setLoading(true);
    try {
      const params = activeFilters.length ? { types: activeFilters.join(",") } : {};
      const { data } = await api.get("/fixtures", { params });
      setFixtures(data);
    } catch { toast.error("Could not load fixtures"); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [activeFilters.join(",")]);

  // Deep-link auto-open: /checkin?fixture_id=…&source=…&venue=…&invite_from=… (QR / pub / share / one-tap invite)
  // Redirected here from /checkin — App.js preserves the query string AND stashes the invite in
  // localStorage so a bounce through login/register/onboarding still lands here with the invite intact.
  const inviteConsumedRef = useRef(false);
  useEffect(() => {
    if (inviteConsumedRef.current) return; // StrictMode double-invoke guard
    const readInvite = () => {
      const params = new URLSearchParams(window.location.search);
      if (params.get("fixture_id") && params.get("source")) {
        return {
          fixture_id: params.get("fixture_id"),
          source: params.get("source"),
          venue: params.get("venue") || "",
          invite_from: params.get("invite_from") || "",
        };
      }
      try {
        const stashed = JSON.parse(localStorage.getItem("fm_invite") || "null");
        if (stashed && stashed.fixture_id && stashed.source && Date.now() - (stashed.ts || 0) < 24 * 60 * 60 * 1000) {
          return stashed;
        }
      } catch { /* ignore */ }
      return null;
    };
    const invite = readInvite();
    if (!invite || (invite.source !== "user" && invite.source !== "official")) return;
    inviteConsumedRef.current = true;
    try { localStorage.removeItem("fm_invite"); } catch { /* ignore */ }
    const cleanUrl = () => { try { window.history.replaceState({}, "", "/fixtures"); } catch { /* ignore */ } };

    (async () => {
      let fx = null;
      try {
        if (invite.source === "user") {
          const { data } = await api.get("/fixtures");
          fx = data.find((f) => f.id === invite.fixture_id);
        } else {
          const { data } = await api.get("/fixtures/upcoming");
          fx = (data.fixtures || []).find((f) => f.id === invite.fixture_id);
        }
      } catch { /* fall through */ }
      if (!fx) {
        toast.error("That fixture isn't available (kickoff may have passed).");
        cleanUrl();
        return;
      }
      setTab(invite.source === "official" ? "upcoming" : "community");

      // One-tap "meet at the ground": invite_from present → auto-check-in + say-hi + jump to chat.
      if (invite.invite_from) {
        try {
          await api.post("/fixtures/checkin", {
            fixture_id: invite.fixture_id, source: invite.source, invisible: false,
            venue: (invite.venue || "").trim() || null,
          });
          const { data } = await api.post("/fixtures/say-hi", {
            fixture_id: invite.fixture_id, target_user_id: invite.invite_from, via_invite: true,
          });
          const matchId = data?.match?.id;
          if (!matchId) throw new Error("say-hi response missing match.id");
          toast.success(data.existed ? "Opening your chat…" : "Match made — say hi!");
          cleanUrl();
          nav(`/chat/${matchId}`);
          return;
        } catch {
          // Fall back to the normal check-in modal so the user isn't stranded.
          toast.error("Couldn't join your mate — opening check-in.");
        }
      }

      // Fallback: open the modal with venue prefilled (QR / share path)
      setCheckinFor({ fixture: fx, source: invite.source, prefillVenue: invite.venue || "" });
      cleanUrl();
    })();
  // eslint-disable-next-line
  }, []);

  const rsvp = async (id) => {
    try {
      const { data } = await api.post(`/fixtures/${id}/rsvp`);
      setFixtures(fx => fx.map(f => f.id === id ? { ...f, is_going: data.going, attendees: data.attendees, attendee_count: data.attendee_count } : f));
      toast.success(data.going ? "You're in — see you at kickoff!" : "RSVP removed");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "RSVP failed"); }
  };

  const remove = async (id) => {
    if (!confirm("Delete this fixture?")) return;
    try {
      await api.delete(`/fixtures/${id}`);
      setFixtures(fx => fx.filter(f => f.id !== id));
      toast.success("Fixture removed");
    } catch { toast.error("Could not delete"); }
  };

  return (
    <div className="min-h-screen bg-footy-bg text-white pb-24 grain">
      <header className="sticky top-0 z-30 bg-footy-bg/90 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-md mx-auto flex items-center justify-between px-5 py-4">
          <h1 className="font-display text-3xl uppercase tracking-wider">Matchday</h1>
          {tab === "community" && (
            <button onClick={()=>setShowCreate(true)} className="w-10 h-10 rounded-full bg-footy-red flex items-center justify-center hover:bg-footy-redHover active:scale-90 transition" data-testid="create-fixture-btn">
              <Plus size={20} />
            </button>
          )}
        </div>
        {/* Tab switcher */}
        <div className="max-w-md mx-auto px-5 pb-3 flex gap-2" data-testid="fixtures-tabs">
          <button onClick={()=>setTab("upcoming")} data-testid="tab-upcoming"
            className={`flex-1 py-2 rounded-full text-xs font-bold uppercase tracking-widest border transition-colors inline-flex items-center justify-center gap-2 ${tab === "upcoming"
              ? "bg-footy-red border-footy-red text-white"
              : "bg-white/5 border-white/15 text-slate-300 hover:bg-white/10"}`}>
            <Trophy size={13} /> Upcoming matches
          </button>
          <button onClick={()=>setTab("community")} data-testid="tab-community"
            className={`flex-1 py-2 rounded-full text-xs font-bold uppercase tracking-widest border transition-colors inline-flex items-center justify-center gap-2 ${tab === "community"
              ? "bg-footy-red border-footy-red text-white"
              : "bg-white/5 border-white/15 text-slate-300 hover:bg-white/10"}`}>
            <Users size={13} /> Community
          </button>
        </div>
        {tab === "community" && myTypes.length > 1 && (
          <div className="max-w-md mx-auto px-5 pb-3 flex items-center gap-2 overflow-x-auto" data-testid="fixtures-filter-chips">
            <SlidersHorizontal size={14} className="text-slate-400 flex-none" />
            {myTypes.map(t => (
              <button key={t} onClick={()=>toggleFilter(t)} data-testid={`fx-filter-${t}`}
                className={`flex-none text-xs font-semibold uppercase tracking-widest px-3 py-1.5 rounded-full border transition-colors ${activeFilters.includes(t)
                  ? "bg-footy-red border-footy-red text-white"
                  : "bg-white/5 border-white/15 text-slate-300 hover:bg-white/10"}`}>
                {labelForConnection(t)}
              </button>
            ))}
            {activeFilters.length > 0 && (
              <button onClick={()=>setActiveFilters([])} className="flex-none text-xs text-slate-400 hover:text-white px-2" data-testid="fx-filter-clear">Clear</button>
            )}
          </div>
        )}
      </header>

      {tab === "community" ? (
      <div className="max-w-md mx-auto px-5 pt-5 space-y-4" data-testid="fixtures-list">
        {(() => {
          // Show the notifications nudge only when there's an active matchday window
          // — any fixture with kickoff within ±3h of right now. Peak intent moment.
          const now = Date.now();
          const WINDOW_MS = 3 * 60 * 60 * 1000;
          const liveWindowOpen = fixtures.some(f => {
            if (!f.kickoff) return false;
            const t = new Date(f.kickoff).getTime();
            return !isNaN(t) && Math.abs(t - now) <= WINDOW_MS;
          });
          return liveWindowOpen ? <EnableNotificationsNudge source="fixtures" variant="matchday" /> : null;
        })()}
        {loading ? (
          <div className="space-y-3">{[1,2,3].map(i => <div key={i} className="h-40 rounded-2xl bg-white/5 animate-pulse" />)}</div>
        ) : fixtures.length === 0 ? (
          <div className="footy-card p-8 text-center">
            <Calendar className="mx-auto text-footy-red mb-3" />
            <h3 className="font-display text-2xl uppercase">No fixtures posted yet</h3>
            <p className="text-slate-400 mt-2 text-sm">Post the game you're heading to and find someone to go with.</p>
            <button onClick={()=>setShowCreate(true)} className="footy-btn-primary mt-6" data-testid="empty-create-btn">Post a fixture</button>
          </div>
        ) : fixtures.map(f => (
          <div key={f.id} className="footy-card overflow-hidden" data-testid={`fixture-${f.id}`}>
            <div className="p-5 flex items-center gap-3" style={{ background: `linear-gradient(90deg, ${clubColor(f.host_club)}30, transparent)` }}>
              <div className="w-3 h-14 rounded-full" style={{ backgroundColor: clubColor(f.host_club) }} />
              <div className="flex-1 min-w-0">
                <div className="font-display text-2xl uppercase tracking-wide leading-none">
                  {f.host_club} <span className="text-slate-500 text-lg">vs</span> {f.opponent}
                </div>
                <div className="text-xs text-slate-400 mt-1 uppercase tracking-widest">{f.competition || "Match day"}</div>
              </div>
              {f.is_host && (
                <button onClick={()=>remove(f.id)} className="text-slate-400 hover:text-footy-red p-2" data-testid={`delete-fixture-${f.id}`}><Trash2 size={16} /></button>
              )}
            </div>
            <div className="px-5 py-3 grid grid-cols-2 gap-3 text-sm text-slate-300 border-t border-white/10">
              <div className="flex items-center gap-2"><Calendar size={14} className="text-footy-red" />{formatKickoff(f.kickoff)}</div>
              <div className="flex items-center gap-2"><MapPin size={14} className="text-footy-red" />{f.venue}</div>
            </div>
            {f.note && <p className="px-5 pb-3 text-sm text-slate-300">{f.note}</p>}
            <div className="px-5 pb-4 flex items-center justify-between">
              <button type="button" onClick={()=>setPickerFor(f)} className="flex items-center gap-2 hover:opacity-80" data-testid={`attendees-${f.id}`}>
                <div className="flex -space-x-2">
                  {f.attendees.slice(0, 4).map(a => (
                    <div key={a.id} className="w-8 h-8 rounded-full border-2 border-footy-card overflow-hidden flex items-center justify-center font-bold text-xs"
                         style={{ background: `linear-gradient(160deg, ${clubColor(a.club)}, #061022)` }} title={a.name}>
                      {a.photo_url ? <img src={photoSrc(a.photo_url)} alt="" className="w-full h-full object-cover" /> : initialsOf(a.name)}
                    </div>
                  ))}
                </div>
                <span className="text-xs text-slate-400 flex items-center gap-1"><Users size={12} /> {f.attendee_count} going</span>
              </button>
              <div className="flex items-center gap-2">
                <button onClick={()=>setCheckinFor({ fixture: f, source: "user" })}
                  className="py-2 px-3 rounded-full text-xs font-bold uppercase tracking-widest border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10 inline-flex items-center gap-1"
                  data-testid={`checkin-open-${f.id}`}>
                  <CheckCircle2 size={12} /> I&apos;m here
                </button>
                <button onClick={()=>rsvp(f.id)} disabled={f.is_host}
                  className={f.is_going ? "footy-btn-secondary !py-2 !px-4 !text-xs" : "footy-btn-primary !py-2 !px-4 !text-xs"}
                  data-testid={`rsvp-${f.id}`}>
                  {f.is_host ? "Hosting" : f.is_going ? "Going ✓" : "Count me in"}
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
      ) : (
        <UpcomingTab userClub={user?.profile?.club} onCheckin={(fixture)=>setCheckinFor({ fixture, source: "official" })} />
      )}

      <AnimatePresence>
        {showCreate && <CreateModal onClose={()=>setShowCreate(false)} onCreated={(fx)=>{ setFixtures(x=>[fx, ...x]); setShowCreate(false); }} userClub={user?.profile?.club} />}
        {pickerFor && (
          <AttendeePicker fixture={pickerFor} meId={user?.id}
            onClose={()=>setPickerFor(null)}
            onPick={(uid)=>connect(pickerFor.id, uid)} />
        )}
        {checkinFor && (
          <CheckinModal
            fixture={checkinFor.fixture}
            source={checkinFor.source}
            prefillVenue={checkinFor.prefillVenue}
            meId={user?.id}
            userClub={user?.profile?.club}
            userClubColor={clubColor(user?.profile?.club)}
            onClose={()=>setCheckinFor(null)}
            onSayHi={async (targetId) => {
              try {
                const { data } = await api.post("/fixtures/say-hi", { fixture_id: checkinFor.fixture.id, target_user_id: targetId });
                toast.success(data.existed ? "Opening your chat…" : "Match made — say hi!");
                setCheckinFor(null);
                nav(`/chat/${data.match.id}`);
              } catch (e) {
                toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not say hi");
              }
            }}
          />
        )}
      </AnimatePresence>

      <BottomNav />
    </div>
  );
}

function CreateModal({ onClose, onCreated, userClub }) {
  const [opponent, setOpponent] = useState("");
  const [kickoff, setKickoff] = useState("");
  const [venue, setVenue] = useState("Home");
  const [competition, setCompetition] = useState("Premier League");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.post("/fixtures", { opponent, kickoff, venue, competition, note });
      toast.success("Fixture posted!");
      onCreated(data);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not post"); }
    finally { setSaving(false); }
  };

  return (
    <motion.div className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center bg-footy-bg/80 backdrop-blur p-0 sm:p-6"
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
      <motion.form onSubmit={submit}
        initial={{ y: 50 }} animate={{ y: 0 }} exit={{ y: 50 }}
        className="w-full max-w-md bg-footy-card border border-white/10 sm:rounded-2xl rounded-t-2xl p-6 pb-[calc(1.5rem+env(safe-area-inset-bottom))] max-h-[92vh] overflow-y-auto"
        data-testid="create-fixture-modal">
        <div className="flex items-center justify-between mb-5">
          <h2 className="font-display text-3xl uppercase">Post a fixture</h2>
          <button type="button" onClick={onClose} className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20" data-testid="close-create-modal"><X size={16} /></button>
        </div>
        <p className="text-sm text-slate-400 mb-5">You're posting as <span className="text-white font-semibold">{userClub || "your club"}</span>. Fans can RSVP to join you.</p>
        <div className="space-y-4">
          <div>
            <label className="footy-label">Opponent</label>
            <input required value={opponent} onChange={(e)=>setOpponent(e.target.value)} placeholder="e.g. Chelsea" className="footy-input" data-testid="fixture-opponent" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="footy-label">Kickoff</label>
              <input required type="datetime-local" value={kickoff} onChange={(e)=>setKickoff(e.target.value)} className="footy-input" data-testid="fixture-kickoff" />
            </div>
            <div>
              <label className="footy-label">Venue</label>
              <select value={venue} onChange={(e)=>setVenue(e.target.value)} className="footy-input" data-testid="fixture-venue">
                <option>Home</option><option>Away</option><option>Neutral</option>
              </select>
            </div>
          </div>
          <div>
            <label className="footy-label">Competition</label>
            <select value={competition} onChange={(e)=>setCompetition(e.target.value)} className="footy-input" data-testid="fixture-competition">
              {["Premier League","Championship","FA Cup","EFL Cup","Champions League","Europa League","Conference League","Scottish Premiership","Friendly","Other"].map(c => <option key={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <label className="footy-label">Note (optional)</label>
            <textarea value={note} onChange={(e)=>setNote(e.target.value)} rows={3} maxLength={200}
              placeholder="Pre-match pint at The Kings? Away travel from Euston 10am." className="footy-input resize-none" data-testid="fixture-note" />
          </div>
          <button type="submit" disabled={saving} className="footy-btn-primary w-full" data-testid="fixture-submit">
            {saving ? "Posting..." : "Post fixture"}
          </button>
        </div>
      </motion.form>
    </motion.div>
  );
}


function AttendeePicker({ fixture, meId, onClose, onPick }) {
  const others = (fixture.attendees || []).filter(a => a.id !== meId);
  return (
    <motion.div className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center bg-footy-bg/80 backdrop-blur p-0 sm:p-6"
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={onClose}>
      <motion.div
        initial={{ y: 50 }} animate={{ y: 0 }} exit={{ y: 50 }}
        onClick={(e)=>e.stopPropagation()}
        className="w-full max-w-md bg-footy-card border border-white/10 sm:rounded-2xl rounded-t-2xl p-6 pb-[calc(1.5rem+env(safe-area-inset-bottom))] max-h-[92vh] overflow-y-auto"
        data-testid="attendee-picker">
        <div className="flex items-center justify-between mb-2">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-footy-red font-bold">Fixture crew</p>
            <h3 className="font-display text-2xl uppercase mt-1">Message an attendee</h3>
          </div>
          <button type="button" onClick={onClose} className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20"><X size={16} /></button>
        </div>
        <p className="text-sm text-slate-400 mb-5">RSVP'd fans skip the swipe queue — go say hi.</p>
        {others.length === 0 ? (
          <p className="text-center text-slate-400 py-8">You're the only one so far. Share the fixture!</p>
        ) : (
          <div className="space-y-2 max-h-[60vh] overflow-y-auto">
            {others.map(a => (
              <button key={a.id} type="button" onClick={()=>onPick(a.id)}
                className="w-full flex items-center gap-3 p-3 rounded-xl bg-white/5 hover:bg-white/10 border border-white/10 transition"
                data-testid={`connect-${a.id}`}>
                <div className="w-11 h-11 rounded-full border-2 flex items-center justify-center font-display text-lg text-white overflow-hidden"
                     style={{ borderColor: clubColor(a.club), background: `linear-gradient(160deg, ${clubColor(a.club)}, #061022)` }}>
                  {a.photo_url ? <img src={a.photo_url} alt="" className="w-full h-full object-cover" /> : initialsOf(a.name)}
                </div>
                <div className="flex-1 text-left">
                  <div className="font-bold leading-tight">{a.name}</div>
                  <div className="text-xs text-slate-400 uppercase tracking-widest">Supports {a.club || "—"}</div>
                </div>
                <MessageCircle size={18} className="text-footy-red" />
              </button>
            ))}
          </div>
        )}
      </motion.div>
    </motion.div>
  );
}


// ---------------- Upcoming tab (Football-Data.org fixtures) ----------------
function UpcomingTab({ userClub, onCheckin }) {
  const nav = useNavigate();
  const [comp, setComp] = useState("");         // "" = all
  const [clubFilter, setClubFilter] = useState(false); // filter to fixtures involving user's club
  const [fixtures, setFixtures] = useState([]);
  const [comps, setComps] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (comp) params.competition = comp;
      if (clubFilter && userClub) params.club = userClub;
      const { data } = await api.get("/fixtures/upcoming", { params });
      setFixtures(data.fixtures || []);
    } catch { toast.error("Could not load upcoming fixtures"); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    api.get("/fixtures/competitions").then(r => setComps(r.data.competitions || [])).catch(() => {});
  }, []);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [comp, clubFilter]);

  return (
    <div className="max-w-md mx-auto px-5 pt-5 space-y-4" data-testid="upcoming-list">
      {/* World Cup teaser card — big driver of engagement in the WC window */}
      <button onClick={()=>nav("/worldcup")}
        className="w-full text-left footy-card p-4 overflow-hidden relative flex items-center gap-3 hover:border-yellow-500/60 transition-colors"
        data-testid="wc-teaser">
        <div className="absolute inset-0 opacity-20 pointer-events-none" style={{
          background: "linear-gradient(90deg, #d4af37 0%, transparent 60%)"
        }} />
        <div className="w-11 h-11 rounded-xl bg-yellow-500/15 text-yellow-400 flex items-center justify-center flex-none relative">
          <Trophy size={20} />
        </div>
        <div className="flex-1 min-w-0 relative">
          <div className="text-[10px] font-bold uppercase tracking-[0.3em] text-yellow-400">World Cup 2026</div>
          <div className="font-display text-lg uppercase leading-tight">Every England game. Pinned.</div>
        </div>
        <span className="text-slate-400 relative">›</span>
      </button>

      <div className="flex items-center gap-2 overflow-x-auto pb-1">
        <button onClick={()=>setComp("")}
          className={`flex-none text-[10px] font-bold uppercase tracking-widest px-3 py-1.5 rounded-full border transition-colors ${!comp ? "bg-footy-red border-footy-red text-white" : "bg-white/5 border-white/15 text-slate-300"}`}
          data-testid="upcoming-comp-all">All</button>
        {comps.map(c => (
          <button key={c.code} onClick={()=>setComp(c.code)} data-testid={`upcoming-comp-${c.code}`}
            className={`flex-none text-[10px] font-bold uppercase tracking-widest px-3 py-1.5 rounded-full border transition-colors ${comp === c.code ? "bg-footy-red border-footy-red text-white" : "bg-white/5 border-white/15 text-slate-300"}`}>
            {c.label}
          </button>
        ))}
        {userClub && (
          <button onClick={()=>setClubFilter(v=>!v)} data-testid="upcoming-my-club"
            className={`flex-none text-[10px] font-bold uppercase tracking-widest px-3 py-1.5 rounded-full border transition-colors ${clubFilter ? "bg-footy-red border-footy-red text-white" : "bg-white/5 border-white/15 text-slate-300"}`}>
            My club
          </button>
        )}
      </div>

      {loading ? (
        <div className="space-y-3">{[1,2,3].map(i => <div key={i} className="h-32 rounded-2xl bg-white/5 animate-pulse" />)}</div>
      ) : fixtures.length === 0 ? (
        <div className="footy-card p-8 text-center">
          <Trophy className="mx-auto text-footy-red mb-3" />
          <h3 className="font-display text-2xl uppercase">No upcoming matches</h3>
          <p className="text-slate-400 mt-2 text-sm">Nothing on the schedule for that filter. Try another competition.</p>
        </div>
      ) : fixtures.map(f => (
        <OfficialFixtureCard key={f.id} f={f} onCheckin={()=>onCheckin(f)} />
      ))}
    </div>
  );
}

function OfficialFixtureCard({ f, onCheckin }) {
  return (
    <div className="footy-card overflow-hidden" data-testid={`official-fixture-${f.id}`}>
      <div className="p-4 flex items-center gap-3 bg-gradient-to-r from-white/5 to-transparent">
        <div className="flex-1 min-w-0">
          <div className="font-display text-xl uppercase tracking-wide leading-tight">
            {f.home_team_short || f.home_team}
            <span className="text-slate-500 text-base mx-2">vs</span>
            {f.away_team_short || f.away_team}
          </div>
          <div className="text-[10px] text-slate-400 mt-1 uppercase tracking-widest">{f.competition_name}{f.matchday ? ` · MD ${f.matchday}` : ""}</div>
        </div>
        <span className="text-[9px] font-bold uppercase tracking-widest px-2 py-1 rounded-full bg-footy-red/15 text-footy-red border border-footy-red/40">Official</span>
      </div>
      <div className="px-4 py-2 grid grid-cols-2 gap-2 text-xs text-slate-300 border-t border-white/10">
        <div className="flex items-center gap-2"><Calendar size={12} className="text-footy-red" />{formatKickoff(f.kickoff)}</div>
        <div className="flex items-center gap-2 truncate"><MapPin size={12} className="text-footy-red" />{f.venue || "TBC"}</div>
      </div>
      <div className="px-4 pb-4 pt-2 flex justify-end">
        <button onClick={onCheckin}
          className="py-2 px-4 rounded-full text-xs font-bold uppercase tracking-widest border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10 inline-flex items-center gap-1.5"
          data-testid={`checkin-open-${f.id}`}>
          <CheckCircle2 size={13} /> I&apos;m at this game
        </button>
      </div>
    </div>
  );
}

// ---------------- Check-in modal (works for both official + user-hosted fixtures) ----------------
function CheckinModal({ fixture, source, prefillVenue, meId, userClub, userClubColor, onClose, onSayHi }) {
  const [state, setState] = useState({ loading: true, me_checked_in: false, invisible: false, my_venue: null, attendees: [], count: 0, venue_counts: {}, error: "" });
  const [busy, setBusy] = useState(false);
  const [venue, setVenue] = useState(prefillVenue || "");

  const kickoffLabel = source === "official"
    ? `${fixture.home_team_short || fixture.home_team} vs ${fixture.away_team_short || fixture.away_team}`
    : `${fixture.host_club} vs ${fixture.opponent}`;

  const load = async () => {
    setState(s => ({ ...s, loading: true, error: "" }));
    try {
      const { data } = await api.get(`/fixtures/${fixture.id}/checkins`, { params: { source } });
      setState({ loading: false, me_checked_in: !!data.me_checked_in, invisible: !!data.invisible,
                 my_venue: data.my_venue || null, attendees: data.attendees || [],
                 count: data.count || 0, venue_counts: data.venue_counts || {}, error: "" });
    } catch (e) {
      setState({ loading: false, me_checked_in: false, invisible: false, my_venue: null,
                 attendees: [], count: 0, venue_counts: {},
                 error: formatApiErrorDetail(e.response?.data?.detail) || "Could not load check-ins" });
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const checkin = async (invisible) => {
    setBusy(true);
    try {
      await api.post("/fixtures/checkin", {
        fixture_id: fixture.id, source, invisible: !!invisible,
        venue: venue.trim() || null,
      });
      toast.success(invisible ? "You're checked in — invisible mode" : "You're at the game! Have a mint one 🍺");
      // Silent add of the home ground to the user's Grounds Book — only when the home
      // team is a canonical club we know. Toast surfaces an Undo action so nothing
      // ends up in the book by accident (e.g. hosting user tests / private fixtures).
      const homeTeam = source === "official"
        ? (fixture.home_team || "")
        : (fixture.host_club || "");
      const isKnownClub = homeTeam && CLUBS.some(c => c.name.toLowerCase() === homeTeam.toLowerCase());
      if (isKnownClub) {
        try {
          const { data: addRes } = await api.post("/grounds/me", { club_name: homeTeam });
          const canonical = addRes?.club_name || homeTeam;
          const groundName = GROUNDS[canonical] || `${canonical}'s ground`;
          toast.success(`${groundName} added to your Grounds Book`, {
            action: {
              label: "Undo",
              onClick: async () => {
                try {
                  await api.delete("/grounds/me", { data: { club_name: canonical } });
                  toast.message(`${groundName} removed`);
                } catch { /* silent */ }
              },
            },
          });
        } catch { /* silent — never block the check-in */ }
      }
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Check-in failed");
    } finally { setBusy(false); }
  };
  const uncheckin = async () => {
    setBusy(true);
    try {
      await api.post("/fixtures/uncheckin", { fixture_id: fixture.id, source, invisible: false });
      toast.success("Checked out");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not check out");
    } finally { setBusy(false); }
  };
  const toggleInvisible = async () => {
    await checkin(!state.invisible);
  };

  return (
    <motion.div className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center bg-footy-bg/85 backdrop-blur p-0 sm:p-6"
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={onClose} data-testid="checkin-modal">
      <motion.div className="w-full max-w-md bg-footy-card border border-white/10 sm:rounded-2xl rounded-t-2xl p-6 pb-[calc(1.5rem+env(safe-area-inset-bottom))] max-h-[88vh] overflow-y-auto"
        initial={{ y: 60 }} animate={{ y: 0 }} exit={{ y: 60 }} onClick={(e)=>e.stopPropagation()}>
        <div className="flex items-start justify-between mb-4">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-footy-red font-bold">Matchday check-in</p>
            <h3 className="font-display text-2xl uppercase mt-1 leading-tight">{kickoffLabel}</h3>
            <div className="text-xs text-slate-400 mt-1">{formatKickoff(fixture.kickoff)}</div>
          </div>
          <button type="button" onClick={onClose} className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20 flex-none ml-3">
            <X size={16} />
          </button>
        </div>

        {state.error && (
          <div className="text-sm text-footy-red bg-footy-red/10 border border-footy-red/30 rounded-lg px-3 py-2 mb-4">{state.error}</div>
        )}

        {!state.me_checked_in ? (
          <div className="text-center py-4" data-testid="checkin-cta">
            <p className="text-sm text-slate-300 mb-4">
              Tap below when you&apos;re at the ground. Other Footymatch fans who&apos;ve checked in will see you — and vice versa.
            </p>
            <div className="mb-4 text-left">
              <label className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Where are you? <span className="text-slate-500 normal-case tracking-normal">(optional)</span></label>
              <div className="mt-1.5 grid grid-cols-3 gap-1.5">
                {[
                  { label: "The ground", val: "The ground" },
                  { label: "The pub", val: "The pub" },
                  { label: "At home", val: "At home" },
                ].map(v => (
                  <button key={v.val} type="button" onClick={()=>setVenue(venue === v.val ? "" : v.val)}
                    className={`py-1.5 rounded-full text-[10px] font-bold uppercase tracking-widest border transition-colors ${venue === v.val ? "bg-footy-red border-footy-red text-white" : "bg-white/5 border-white/15 text-slate-300 hover:bg-white/10"}`}
                    data-testid={`checkin-venue-preset-${v.val.replace(/\s+/g,'-').toLowerCase()}`}>
                    {v.label}
                  </button>
                ))}
              </div>
              <input value={venue} onChange={(e)=>setVenue(e.target.value.slice(0,80))}
                placeholder="Or type a pub / venue name…" maxLength={80}
                className="footy-input mt-2 !py-2 !text-sm" data-testid="checkin-venue-input" />
            </div>
            <button onClick={()=>checkin(false)} disabled={busy}
              className="footy-btn-primary w-full disabled:opacity-50" data-testid="checkin-confirm">
              <CheckCircle2 size={16} /> {busy ? "Checking in…" : "I'm at the game"}
            </button>
            <button onClick={()=>checkin(true)} disabled={busy}
              className="w-full mt-2 text-xs text-slate-400 hover:text-white uppercase tracking-widest inline-flex items-center justify-center gap-1 disabled:opacity-50"
              data-testid="checkin-invisible">
              <EyeOff size={12} /> Check in invisibly (only you see others)
            </button>
            <p className="text-[10px] text-slate-500 mt-3">Only available from 3h before kickoff to 3h after full time.</p>
          </div>
        ) : (
          <div data-testid="checkin-attendees">
            <div className="flex items-center justify-between mb-4 p-3 rounded-xl bg-emerald-500/10 border border-emerald-500/30">
              <div className="flex items-center gap-2 text-sm">
                <CheckCircle2 size={16} className="text-emerald-400" />
                <span className="text-emerald-300 font-semibold">You&apos;re checked in{state.invisible ? " (invisible)" : ""}</span>
              </div>
              <button onClick={uncheckin} disabled={busy}
                className="text-[10px] text-slate-400 hover:text-footy-red uppercase tracking-widest disabled:opacity-50" data-testid="checkin-checkout">Check out</button>
            </div>
            <ShareRow fixture={fixture} source={source} userClub={userClub} userClubColor={userClubColor} />
            <div className="flex items-center justify-between mb-4">
              <div className="text-xs text-slate-400">
                {state.count} other {state.count === 1 ? "fan is" : "fans are"} here
              </div>
              <button onClick={toggleInvisible} disabled={busy}
                className="text-[10px] text-slate-400 hover:text-white uppercase tracking-widest inline-flex items-center gap-1 disabled:opacity-50"
                data-testid="checkin-toggle-invisible">
                <EyeOff size={12} /> {state.invisible ? "Go visible" : "Go invisible"}
              </button>
            </div>

            {state.attendees.length === 0 ? (
              <p className="text-center text-slate-400 py-6 text-sm">Nobody else has checked in yet. Come back at kickoff.</p>
            ) : (
              <div className="space-y-2">
                {state.attendees.map(a => (
                  <div key={a.id} className="flex items-center gap-3 p-3 rounded-xl bg-white/5 border border-white/10" data-testid={`checkin-attendee-${a.id}`}>
                    <div className="w-11 h-11 rounded-full border-2 flex items-center justify-center font-display text-lg text-white overflow-hidden flex-none"
                         style={{ borderColor: clubColor(a.club), background: `linear-gradient(160deg, ${clubColor(a.club)}, #061022)` }}>
                      {a.photo_url ? <img src={photoSrc(a.photo_url)} alt="" className="w-full h-full object-cover" /> : initialsOf(a.name)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-bold leading-tight truncate flex items-center gap-2">
                        {a.name}
                        {a.venue && state.my_venue && a.venue.toLowerCase() === state.my_venue.toLowerCase() && (
                          <span className="text-[9px] font-bold uppercase tracking-widest px-1.5 py-0.5 rounded-full bg-emerald-500/20 text-emerald-300 border border-emerald-500/40" title="Same venue as you">Here too</span>
                        )}
                      </div>
                      <div className="text-[10px] text-slate-400 uppercase tracking-widest truncate">
                        Supports {a.club || "—"}{a.venue ? ` · ${a.venue}` : ""}
                      </div>
                    </div>
                    <button onClick={()=>onSayHi(a.id)}
                      className="py-2 px-3 rounded-full text-[10px] font-bold uppercase tracking-widest bg-footy-red hover:bg-footy-redHover text-white inline-flex items-center gap-1 flex-none"
                      data-testid={`say-hi-${a.id}`}>
                      <Handshake size={12} /> Say hi
                    </button>
                  </div>
                ))}
              </div>
            )}

            <VenueQRSection fixture={fixture} source={source} myVenue={state.my_venue} />
            <InviteMateRow fixture={fixture} source={source} meId={meId} myVenue={state.my_venue} />
          </div>
        )}
      </motion.div>
    </motion.div>
  );
}

// ---------------- Share row (branded match-day card) ----------------
function ShareRow({ fixture, source, userClub, userClubColor }) {
  const { user } = useAuth();
  // Determine which mode makes sense by default: if the user's club is the AWAY
  // side of an official fixture, default to "away"; otherwise "home".
  const homeTeam = source === "official" ? (fixture.home_team_short || fixture.home_team) : fixture.host_club;
  const isUserAway = userClub && homeTeam && userClub.trim().toLowerCase() !== homeTeam.trim().toLowerCase();
  const [mode, setMode] = useState(isUserAway ? "away" : "home");
  const [busy, setBusy] = useState(false);

  const refCode = user?.referral_code || "";
  const shareLandingUrl = refCode ? `https://footymatch.co.uk/?ref=${refCode}` : "https://footymatch.co.uk";

  const buildUrl = () => {
    const params = new URLSearchParams({
      source, mode,
      club_color: userClubColor || "#152A55",
    });
    return `${process.env.REACT_APP_BACKEND_URL}/api/share/matchday/${encodeURIComponent(fixture.id)}?${params.toString()}`;
  };

  const shareText = () => {
    if (mode === "away") {
      const opp = source === "official" ? (fixture.home_team_short || fixture.home_team) : fixture.host_club;
      return `Away day — heading to ${opp} today. Find fellow football fans on FootyMatch → ${shareLandingUrl}`;
    }
    const home = source === "official" ? (fixture.home_team_short || fixture.home_team) : fixture.host_club;
    const away = source === "official" ? (fixture.away_team_short || fixture.away_team) : fixture.opponent;
    return `I'm at ${fixture.venue || "the ground"} today for ${home} v ${away}. Find football friends on FootyMatch → ${shareLandingUrl}`;
  };

  const shareNow = async () => {
    setBusy(true);
    try {
      const url = buildUrl();
      const resp = await fetch(url, { credentials: "include" });
      if (!resp.ok) throw new Error("Could not build share card");
      const blob = await resp.blob();
      const file = new File([blob], `footymatch-${mode}-${fixture.id}.png`, { type: "image/png" });

      // 1st choice: native Web Share API with the file (mobile IG / WhatsApp / etc.)
      const shareData = { title: "Footymatch", text: shareText(), files: [file], url: shareLandingUrl };
      if (navigator.canShare && navigator.canShare(shareData)) {
        await navigator.share(shareData);
        toast.success("Shared 🎉");
        return;
      }
      // 2nd choice: text-only Web Share (desktop Chrome/Edge)
      if (navigator.share) {
        try {
          await navigator.share({ title: "Footymatch", text: shareText(), url: shareLandingUrl });
          toast.success("Shared 🎉");
          return;
        } catch { /* user cancelled or unsupported — fall through to download */ }
      }
      // 3rd choice: download the PNG so the user can post it manually
      const a = document.createElement("a");
      const objectUrl = URL.createObjectURL(blob);
      a.href = objectUrl;
      a.download = `footymatch-${mode}-${fixture.id}.png`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
      toast.success("Downloaded — post it to your socials!");
    } catch (e) {
      toast.error(e.message || "Could not share");
    } finally { setBusy(false); }
  };

  return (
    <div className="mb-4 p-3 rounded-xl bg-white/5 border border-white/10" data-testid="share-row">
      <div className="flex items-center gap-2 mb-3">
        <Share2 size={14} className="text-footy-red" />
        <span className="text-sm font-semibold">Brag about it 📸</span>
      </div>
      <div className="grid grid-cols-2 gap-2 mb-3">
        <button type="button" onClick={()=>setMode("home")} data-testid="share-mode-home"
          className={`py-2 rounded-full text-[10px] font-bold uppercase tracking-widest border transition-colors inline-flex items-center justify-center gap-1.5 ${mode === "home" ? "bg-footy-red border-footy-red text-white" : "bg-white/5 border-white/15 text-slate-300 hover:bg-white/10"}`}>
          <MapPin size={11} /> Matchday
        </button>
        <button type="button" onClick={()=>setMode("away")} data-testid="share-mode-away"
          className={`py-2 rounded-full text-[10px] font-bold uppercase tracking-widest border transition-colors inline-flex items-center justify-center gap-1.5 ${mode === "away" ? "bg-footy-red border-footy-red text-white" : "bg-white/5 border-white/15 text-slate-300 hover:bg-white/10"}`}>
          <Bus size={11} /> Away day
        </button>
      </div>
      <button type="button" onClick={shareNow} disabled={busy}
        className="footy-btn-primary w-full disabled:opacity-50" data-testid="share-generate-btn">
        <Share2 size={14} /> {busy ? "Preparing…" : "Share your matchday card"}
      </button>
      <p className="text-[10px] text-slate-500 text-center mt-2">Post to Instagram, WhatsApp, or Facebook — help spread the word.</p>
    </div>
  );
}


// ---------------- Venue QR — for pub/group check-ins ----------------
function VenueQRSection({ fixture, source, myVenue }) {
  const [open, setOpen] = useState(false);
  const [qrUrl, setQrUrl] = useState(null);

  // Deep link that pre-selects this fixture + venue for anyone who scans.
  const deepLink = (() => {
    const params = new URLSearchParams({ fixture_id: fixture.id, source });
    if (myVenue) params.set("venue", myVenue);
    return `https://footymatch.co.uk/checkin?${params.toString()}`;
  })();

  const load = async () => {
    if (qrUrl) return;
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/qr?data=${encodeURIComponent(deepLink)}`;
    try {
      const resp = await fetch(url, { credentials: "include" });
      if (!resp.ok) throw new Error("Could not build QR");
      const blob = await resp.blob();
      setQrUrl(URL.createObjectURL(blob));
    } catch { /* silent — user gets no QR image */ }
  };

  const shareQr = async () => {
    if (!qrUrl) await load();
    const resp = await fetch(qrUrl);
    const blob = await resp.blob();
    const file = new File([blob], `footymatch-qr-${fixture.id}.png`, { type: "image/png" });
    const shareData = {
      title: "Footymatch",
      text: `Scan to join us${myVenue ? ` at ${myVenue}` : ""} — check into this game on Footymatch.`,
      files: [file],
      url: deepLink,
    };
    if (navigator.canShare && navigator.canShare(shareData)) {
      try { await navigator.share(shareData); toast.success("QR shared 🎉"); return; } catch { /* ignore */ }
    }
    // Fallback: open the QR in a new tab so the user can screenshot / save it
    window.open(qrUrl, "_blank", "noopener");
  };

  return (
    <div className="mt-4 rounded-xl bg-white/5 border border-white/10 p-3" data-testid="venue-qr-section">
      <button type="button" onClick={()=>{ setOpen(o=>!o); if(!open) load(); }}
        className="w-full flex items-center gap-3 text-left"
        data-testid="venue-qr-toggle">
        <div className="w-9 h-9 rounded-lg bg-footy-red/15 text-footy-red flex items-center justify-center flex-none">
          <QrCode size={16} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-bold text-sm">Pub QR code</div>
          <p className="text-xs text-slate-400 mt-0.5">
            {myVenue ? `Show this at ${myVenue}` : "Print or share this — mates scan to join your check-in."}
          </p>
        </div>
        <span className="text-slate-400 text-lg" aria-hidden>{open ? "−" : "+"}</span>
      </button>
      {open && (
        <div className="mt-3 flex items-center gap-3">
          <div className="w-28 h-28 rounded-lg bg-white p-1.5 flex-none">
            {qrUrl ? (
              <img src={qrUrl} alt="Venue check-in QR" className="w-full h-full object-contain" data-testid="venue-qr-img" />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-slate-400 text-[10px]">Generating…</div>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[10px] text-slate-500 uppercase tracking-widest">Scan or share</p>
            <p className="text-[11px] text-slate-300 break-all font-mono">{deepLink}</p>
            <button onClick={shareQr} className="mt-2 text-[10px] font-bold uppercase tracking-widest px-3 py-1.5 rounded-full bg-footy-red hover:bg-footy-redHover text-white inline-flex items-center gap-1" data-testid="venue-qr-share">
              <Share2 size={11} /> Share QR
            </button>
          </div>
        </div>
      )}
    </div>
  );
}



// ---------------- One-tap "meet at the ground" invite ----------------
// Host clicks Invite mate → gets a URL like /checkin?fixture_id=…&source=…&venue=…&invite_from=<meId>
// Recipient taps → auto-checks-in → auto-say-hi → lands in /chat/{match_id} with the host.
function InviteMateRow({ fixture, source, meId, myVenue }) {
  const [busy, setBusy] = useState(false);
  const [showTip, setShowTip] = useState(() => {
    try { return !localStorage.getItem("fm_seen_invite_tip"); } catch { return false; }
  });
  const dismissTip = () => {
    setShowTip(false);
    try { localStorage.setItem("fm_seen_invite_tip", "1"); } catch { /* ignore */ }
  };

  const inviteUrl = (() => {
    const params = new URLSearchParams({ fixture_id: fixture.id, source, invite_from: meId || "" });
    if (myVenue) params.set("venue", myVenue);
    return `https://footymatch.co.uk/checkin?${params.toString()}`;
  })();

  const opp = source === "official"
    ? `${fixture.home_team_short || fixture.home_team} v ${fixture.away_team_short || fixture.away_team}`
    : `${fixture.host_club} v ${fixture.opponent}`;
  const shareText = `Coming to ${opp}? I'm${myVenue ? ` at ${myVenue}` : ""} — tap this and we'll be matched on Footymatch 👇`;

  const invite = async () => {
    dismissTip();
    setBusy(true);
    try {
      const shareData = { title: "Meet me at the game", text: shareText, url: inviteUrl };
      if (navigator.share) {
        try { await navigator.share(shareData); toast.success("Invite sent 🎉"); return; }
        catch { /* user cancelled — fall through to clipboard */ }
      }
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(inviteUrl);
        toast.success("Invite link copied — paste it to your mate on WhatsApp.");
        return;
      }
      // Last-resort fallback: legacy execCommand copy
      const ta = document.createElement("textarea");
      ta.value = inviteUrl; document.body.appendChild(ta); ta.select();
      try { document.execCommand("copy"); toast.success("Invite link copied."); }
      catch { toast.error("Could not copy — long-press the link to copy it manually."); }
      finally { ta.remove(); }
    } finally { setBusy(false); }
  };

  return (
    <div className={`mt-3 rounded-xl border p-3 ${showTip ? "bg-footy-red/15 border-footy-red/50 ring-1 ring-footy-red/40" : "bg-footy-red/10 border-footy-red/30"}`} data-testid="invite-mate-row">
      {showTip && (
        <div className="mb-2 -mt-1 flex items-start gap-2 text-[11px] text-footy-red/95" data-testid="invite-mate-tip">
          <span className="font-bold uppercase tracking-widest">New</span>
          <span className="flex-1">Send a mate this link — they'll land straight in your chat, no swiping.</span>
          <button onClick={dismissTip} aria-label="Dismiss tip" data-testid="invite-mate-tip-dismiss"
            className="text-slate-400 hover:text-white"><X size={12} /></button>
        </div>
      )}
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 rounded-lg bg-footy-red/25 text-footy-red flex items-center justify-center flex-none">
          <UserPlus size={16} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-bold text-sm">Invite a specific mate</div>
          <p className="text-xs text-slate-400 mt-0.5">
            One tap and they land straight in a chat with you — no swiping, no waiting.
          </p>
          <button onClick={invite} disabled={busy || !meId}
            className="mt-2 footy-btn-primary !py-2 !px-4 !text-xs disabled:opacity-50"
            data-testid="invite-mate-share">
            <Copy size={12} /> {busy ? "Preparing…" : "Send invite link"}
          </button>
        </div>
      </div>
    </div>
  );
}
